import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

// VAPID public key - safe to expose in frontend
const VAPID_PUBLIC_KEY = 'BOyM3BVc1bUpXiwuI0kagLHPzdoGbL75yUcWbodq7FTZxR8avyHiA_52zR2Ljo5sFWig9jEL61bDxTTesWIKzIg';

interface WebPushState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length) as Uint8Array<ArrayBuffer>;
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function useWebPushNotifications() {
  const { user } = useUser();
  const [state, setState] = useState<WebPushState>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    isLoading: true,
    error: null,
  });

  // Check if Web Push is supported
  const checkSupport = useCallback(() => {
    const isSupported = 'serviceWorker' in navigator && 
                        'PushManager' in window && 
                        'Notification' in window;
    return isSupported;
  }, []);

  // Get current subscription status
  const checkSubscription = useCallback(async () => {
    if (!checkSupport()) {
      setState(prev => ({ ...prev, isSupported: false, isLoading: false }));
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      const permission = Notification.permission;
      
      setState(prev => ({
        ...prev,
        isSupported: true,
        permission,
        isSubscribed: !!subscription,
        isLoading: false,
      }));
    } catch (error) {
      console.error('[WebPush] Error checking subscription:', error);
      setState(prev => ({
        ...prev,
        isSupported: true,
        isLoading: false,
        error: 'Failed to check notification status',
      }));
    }
  }, [checkSupport]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, error: 'You must be logged in' }));
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        setState(prev => ({
          ...prev,
          permission,
          isLoading: false,
          error: permission === 'denied' 
            ? 'Notifications are blocked. Please enable them in your browser settings.' 
            : 'Notification permission was not granted',
        }));
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      console.log('[WebPush] Subscribed:', subscription.endpoint);

      // Store the subscription in database
      const subscriptionJson = JSON.stringify(subscription.toJSON());
      
      const { error: upsertError } = await supabase
        .from('push_tokens')
        .upsert(
          {
            user_id: user.id,
            token: subscriptionJson,
            platform: 'web',
            is_active: true,
            device_info: {
              userAgent: navigator.userAgent,
              language: navigator.language,
              platform: navigator.platform,
            },
          },
          {
            onConflict: 'user_id,platform',
          }
        );

      if (upsertError) {
        console.error('[WebPush] Error storing subscription:', upsertError);
        throw new Error('Failed to save notification settings');
      }

      setState(prev => ({
        ...prev,
        permission: 'granted',
        isSubscribed: true,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      console.error('[WebPush] Subscribe error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to enable notifications',
      }));
      return false;
    }
  }, [user]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!user) return false;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Deactivate the token in database
      await supabase
        .from('push_tokens')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('platform', 'web');

      setState(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      console.error('[WebPush] Unsubscribe error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to disable notifications',
      }));
      return false;
    }
  }, [user]);

  // Initialize on mount
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Re-check when user changes
  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user, checkSubscription]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    refresh: checkSubscription,
  };
}
